import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import Sidebar from '../components/layout/Sidebar';
import Header from '../components/layout/Header';
import DocumentUpload from '../components/vault/DocumentUpload';
import operatorService from '../services/operatorService';
import { useAuth } from '../hooks';
import '../styles/Vault.css';
import '../styles/Dashboard.css';

const Vault = () => {
  const { currentUser, logout } = useAuth();
  const [showUserMenu, setShowUserMenu] = useState(false);
  const navigate = useNavigate();

  const [vaultItems, setVaultItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedFramework, setSelectedFramework] = useState('');
  const [expandedItems, setExpandedItems] = useState(new Set());
  const [viewMode, setViewMode] = useState('all'); // 'all', 'documents', 'queries'
  const [showUpload, setShowUpload] = useState(false);

  const frameworks = [
    { code: '', name: 'All Frameworks' },
    { code: 'MiCA', name: 'MiCA' },
    { code: 'DORA', name: 'DORA' },
    { code: 'GDPR', name: 'GDPR' },
    { code: 'AML', name: 'AML/CFT' },
    { code: 'MiFID_II', name: 'MiFID II' },
    { code: 'NIS2', name: 'NIS2' }
  ];

  useEffect(() => {
    loadVaultItems();
  }, []);

  const loadVaultItems = async () => {
    setLoading(true);
    try {
      const response = await operatorService.getVaultItems();
      setVaultItems(response.items || []);
    } catch (error) {
      console.error('Failed to load vault items:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (vaultId) => {
    if (!window.confirm('Delete this saved query? This action cannot be undone.')) {
      return;
    }

    try {
      await operatorService.deleteVaultItem(vaultId);
      setVaultItems(items => items.filter(item => item.id !== vaultId));
    } catch (error) {
      console.error('Failed to delete vault item:', error);
      alert('Failed to delete item');
    }
  };

  const toggleExpand = (vaultId) => {
    const newExpanded = new Set(expandedItems);
    if (newExpanded.has(vaultId)) {
      newExpanded.delete(vaultId);
    } else {
      newExpanded.add(vaultId);
    }
    setExpandedItems(newExpanded);
  };

  const exportToCSV = () => {
    const csvRows = [
      ['Date', 'Query', 'Answer', 'Frameworks', 'Sources', 'Confidence']
    ];

    filteredItems.forEach(item => {
      const date = new Date(item.saved_at).toLocaleDateString();
      const query = item.query.replace(/"/g, '""');
      const answer = item.answer.replace(/"/g, '""');
      const frameworks = item.frameworks.join('; ');
      const sources = item.sources.map(s => s.title).join('; ');
      const confidence = item.confidence;

      csvRows.push([
        date,
        `"${query}"`,
        `"${answer}"`,
        frameworks,
        `"${sources}"`,
        confidence
      ]);
    });

    const csvContent = csvRows.map(row => row.join(',')).join('\n');
    const blob = new Blob([csvContent], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.setAttribute('href', url);
    a.setAttribute('download', `vault_export_${Date.now()}.csv`);
    a.click();
  };

  // Separate documents and queries
  const documents = vaultItems.filter(item => item.type === 'vault_document');
  const queries = vaultItems.filter(item => item.type === 'vault_query');

  const filteredItems = vaultItems.filter(item => {
    // View mode filter
    if (viewMode === 'documents' && item.type !== 'vault_document') return false;
    if (viewMode === 'queries' && item.type !== 'vault_query') return false;

    // Search filter
    if (searchQuery) {
      if (item.type === 'vault_document') {
        if (!item.document_name.toLowerCase().includes(searchQuery.toLowerCase())) {
          return false;
        }
      } else {
        if (!item.query?.toLowerCase().includes(searchQuery.toLowerCase()) &&
            !item.answer?.toLowerCase().includes(searchQuery.toLowerCase())) {
          return false;
        }
      }
    }

    // Framework filter
    if (selectedFramework && item.frameworks && !item.frameworks.includes(selectedFramework)) {
      return false;
    }

    return true;
  });

  return (
    <>
      <Header
        currentUser={currentUser}
        showUserMenu={showUserMenu}
        setShowUserMenu={setShowUserMenu}
        onLogout={logout}
      />

      <div className="app">
        <Sidebar />

        <main className="main-content">
          <div className="content-area">
            {/* Hero Header */}
            <div className="vault-hero">
              <div className="vault-hero-content">
                <div className="vault-hero-label">COMPLIANCE TERMINAL</div>
                <h1 className="vault-hero-title">Vault</h1>
                <p className="vault-hero-description">
                  Your saved compliance queries and regulatory intelligence. Export, organize, and reference critical compliance guidance.
                </p>
              </div>
            </div>

            {/* View Mode Tabs */}
            <div className="vault-tabs">
              <button
                className={`vault-tab ${viewMode === 'all' ? 'active' : ''}`}
                onClick={() => setViewMode('all')}
              >
                All ({vaultItems.length})
              </button>
              <button
                className={`vault-tab ${viewMode === 'documents' ? 'active' : ''}`}
                onClick={() => setViewMode('documents')}
              >
                Documents ({documents.length})
              </button>
              <button
                className={`vault-tab ${viewMode === 'queries' ? 'active' : ''}`}
                onClick={() => setViewMode('queries')}
              >
                ORACLE Queries ({queries.length})
              </button>
            </div>

            {/* Filters and Actions */}
            <div className="vault-controls">
              <div className="vault-search">
                <input
                  type="text"
                  className="vault-search-input"
                  placeholder={viewMode === 'documents' ? "Search documents..." : "Search queries..."}
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                />
              </div>

              <div className="vault-filters">
                <select
                  className="vault-filter-select"
                  value={selectedFramework}
                  onChange={(e) => setSelectedFramework(e.target.value)}
                >
                  {frameworks.map(fw => (
                    <option key={fw.code} value={fw.code}>{fw.name}</option>
                  ))}
                </select>

                <button
                  className="vault-action-btn primary"
                  onClick={() => setShowUpload(!showUpload)}
                >
                  {showUpload ? 'Hide Upload' : 'Upload Document'}
                </button>

                <button className="vault-action-btn" onClick={exportToCSV}>
                  Export CSV
                </button>
              </div>
            </div>

            {/* Upload Section */}
            {showUpload && (
              <div className="vault-upload-section">
                <DocumentUpload
                  currentUser={currentUser}
                  onUploadComplete={() => {
                    setShowUpload(false);
                    loadVaultItems();
                  }}
                />
              </div>
            )}

            {/* Vault Items */}
            {loading ? (
              <div className="vault-loading">Loading saved queries...</div>
            ) : filteredItems.length === 0 ? (
              <div className="vault-empty">
                <div className="empty-icon">◈</div>
                <h3 className="empty-title">No Saved Queries</h3>
                <p className="empty-description">
                  Save queries from ORACLE by clicking "Save to Vault" on any response.
                </p>
                <button
                  className="vault-btn-primary"
                  onClick={() => navigate('/dashboard/compliance-operator')}
                >
                  Go to ORACLE
                </button>
              </div>
            ) : (
              <div className="vault-items-container">
                <div className="vault-items-header">
                  <span className="vault-count">{filteredItems.length} saved {filteredItems.length === 1 ? 'query' : 'queries'}</span>
                </div>

                <div className="vault-items-list">
                  {filteredItems.map(item => {
                    const isExpanded = expandedItems.has(item.id);
                    const savedDate = new Date(item.saved_at || item.uploaded_at).toLocaleDateString('en-US', {
                      year: 'numeric',
                      month: 'short',
                      day: 'numeric'
                    });

                    // Render document item
                    if (item.type === 'vault_document') {
                      return (
                        <div key={item.id} className="vault-item-card document">
                          <div className="vault-item-header">
                            <div className="vault-item-meta">
                              <span className="vault-item-date">{savedDate}</span>
                              <span className="document-type-badge">
                                {item.mime_type === 'application/pdf' ? 'PDF' :
                                 item.mime_type === 'application/vnd.openxmlformats-officedocument.wordprocessingml.document' ? 'DOCX' : 'TXT'}
                              </span>
                              <span className="file-size-badge">
                                {(item.file_size / 1024).toFixed(1)} KB
                              </span>
                            </div>
                          </div>

                          <div className="vault-item-content">
                            <div className="vault-document-name">
                              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/>
                                <polyline points="14 2 14 8 20 8"/>
                              </svg>
                              {item.document_name}
                            </div>

                            {item.tags && item.tags.length > 0 && (
                              <div className="vault-item-tags">
                                {item.tags.map((tag, idx) => (
                                  <span key={idx} className="tag-badge">{tag}</span>
                                ))}
                              </div>
                            )}
                          </div>

                          <div className="vault-item-actions">
                            <button className="vault-item-btn">
                              Download
                            </button>
                            <button
                              className="vault-item-btn delete"
                              onClick={() => handleDelete(item.id)}
                            >
                              Delete
                            </button>
                          </div>
                        </div>
                      );
                    }

                    // Render query item (existing code)
                    return (
                      <div key={item.id} className="vault-item-card query">
                        <div className="vault-item-header">
                          <div className="vault-item-meta">
                            <span className="vault-item-date">{savedDate}</span>
                            {item.frameworks && item.frameworks.length > 0 && (
                              <div className="vault-item-frameworks">
                                {item.frameworks.map(fw => (
                                  <span key={fw} className="framework-tag">{fw}</span>
                                ))}
                              </div>
                            )}
                            <span className={`confidence-badge ${item.confidence.toLowerCase()}`}>
                              {item.confidence}
                            </span>
                          </div>
                        </div>

                        <div className="vault-item-content">
                          <div className="vault-item-query">
                            <strong>Q:</strong> {item.query}
                          </div>

                          <div className={`vault-item-answer ${isExpanded ? 'expanded' : ''}`}>
                            <strong>A:</strong> {item.answer}
                          </div>

                          {item.answer.length > 300 && (
                            <button
                              className="vault-expand-btn"
                              onClick={() => toggleExpand(item.id)}
                            >
                              {isExpanded ? 'Show less' : 'Show more'}
                            </button>
                          )}

                          {item.sources && item.sources.length > 0 && (
                            <div className="vault-item-sources">
                              <div className="sources-label">Sources ({item.sources.length})</div>
                              <div className="sources-list">
                                {item.sources.slice(0, 3).map((source, idx) => (
                                  <div key={idx} className="source-tag">
                                    {source.title || source.name || 'Document'}
                                  </div>
                                ))}
                                {item.sources.length > 3 && (
                                  <span className="sources-more">+{item.sources.length - 3} more</span>
                                )}
                              </div>
                            </div>
                          )}
                        </div>

                        <div className="vault-item-actions">
                          <button
                            className="vault-item-btn delete"
                            onClick={() => handleDelete(item.id)}
                          >
                            Delete
                          </button>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}
          </div>
        </main>
      </div>
    </>
  );
};

export default Vault;
